/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import {
  AfterContentInit,
  booleanAttribute,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnDestroy,
  Output,
  QueryList,
  signal,
  ViewChildren,
  ChangeDetectionStrategy,
} from '@angular/core'
import { PidxScrollToService, WINDOW } from '@myprofile-ui/pidx-utils'
import { Actions, Store, ofActionSuccessful } from '@ngxs/store'
import { Observable, Subject, lastValueFrom, mergeMap, of } from 'rxjs'
import { filter, pairwise, startWith, take, takeUntil } from 'rxjs/operators'
import { MyProfileAddressEditFormComponent } from './components/myprofile-address-editform/myprofile-address-editform.component'
import { MyProfileAddressListMode } from './enums/myprofile-address-list-mode'
import { MyProfileAddressWidgetMode } from './enums/myprofile-address-widget-mode'
import { AddressSelectedEvent } from './interfaces/myprofile-address-selected-event.interface'
import { MyProfileAddressListService } from './services/myprofile-address-list.service'
import { SetInitialConfiguration } from './store/address-widget.actions'
import { CreateAddress, DeleteAddress, ShowBlankEditor, UpdateAddress } from './store/editor.actions'
import { GetAddresses, GetProfile, SelectAddress } from './store/list.actions'
import { RESTRICT_GLOBAL_UI_CHANGES } from '../../../pidx-utils/src/lib/helpers/restrictGlobalUIChanges.injectionToken'
import { AddressDto } from '../../../../apps/myprofile-ui/src/app/services/address/address.service'
import { v4 as uuid } from 'uuid'

@Component({
  selector: 'myprofile-address-widget-ng',
  templateUrl: './myprofile-address-widget.component.html',
  host: { class: 'profile:block profile:relative profile:break-words' },
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MyProfileAddressWidgetComponent implements AfterContentInit, OnDestroy {
  private window = inject<Window>(WINDOW)
  private scrollToService = inject(PidxScrollToService)
  private store = inject(Store)
  private actions$ = inject(Actions)
  private myProfileAddressListService = inject(MyProfileAddressListService)

  @Input() mode: MyProfileAddressWidgetMode = MyProfileAddressWidgetMode.CHECKOUT
  @Input() scrollOnAddNew = false
  @Input() locale = 'en_GB'
  @Input() country = 'gb'
  @Input() token!: string
  @Input() identifier?: string
  @Input() shouldShowHeadline = false
  @Input() initiallySelectedAddressId?: string

  /**
   * When `true`, the widget announces its own initial-load status via a polite
   * live region using its bundled translation pipeline (translated key
   * `addressWidget.general.loadingStatus`). Default `false` keeps the widget
   * silent during initial load so a host page can orchestrate one global
   * announcement; set to `true` for standalone embeddings.
   *
   * Does not gate the in-editor saving overlay (always announced because it is
   * user-initiated).
   */
  public announceLoading = input(false, { transform: booleanAttribute })

  @Output() addressSelected = new EventEmitter<string | AddressSelectedEvent>()
  @Output() addressChange: EventEmitter<void> = new EventEmitter<void>()
  @Output() addressUpdated: EventEmitter<void> = new EventEmitter<void>()
  @Output() addressCreated: EventEmitter<void> = new EventEmitter<void>()
  @Output() addressDeleted: EventEmitter<void> = new EventEmitter<void>()

  addresses$: Observable<AddressDto[]> = inject(Store).select(({ addressWidget }) => addressWidget.addresses)
  isLoading$: Observable<boolean> = inject(Store).select(({ addressWidget }) => addressWidget.loading)
  isCnUser$: Observable<boolean> = inject(Store).select(({ addressWidget }) => addressWidget.isCnUser)

  public addressBeingEdited$: Observable<string | null> = of(null)
  public showEditor$: Observable<boolean> = of(false)
  public selectedAddressId$: Observable<string | null> = of(null)
  public errorMessage$: Observable<string | null> = of(null)
  public MyProfileAddressWidgetMode = MyProfileAddressWidgetMode
  public MyProfileAddressListMode = MyProfileAddressListMode
  public instanceId!: string
  public shouldShowHideSection = signal(false)
  public errorWhileInitializing = signal(false)
  public MAX_ADDRESSES = 5
  public restrictGlobalUIChanges = inject<boolean>(RESTRICT_GLOBAL_UI_CHANGES)

  @ViewChildren(MyProfileAddressEditFormComponent, { read: ElementRef }) public addressEditFormQL!: QueryList<ElementRef>

  private unsubscribe$ = new Subject<void>()

  /** Page-level fallback id (e.g. completion-status CTA) used when no in-widget add button is rendered. */
  private externalFocusReturnId: string | null = null

  public async ngAfterContentInit() {
    this.actions$
      .pipe(
        ofActionSuccessful(SelectAddress),
        filter(({ instanceId }) => instanceId === this.instanceId),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(({ selectedAddressId }) => {
        if (!this.identifier) {
          this.addressSelected.emit(selectedAddressId)
        } else {
          this.addressSelected.emit({ selectedAddressId, identifier: this.identifier })
        }
      })

    this.actions$
      .pipe(
        ofActionSuccessful(ShowBlankEditor),
        filter(({ instanceId }) => instanceId === this.instanceId),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(({ visible }) => {
        const addresses = this.store.selectSnapshot(({ addressWidget }) => addressWidget.addresses)
        this.shouldShowHideSection.set(visible && addresses && addresses.length === 0)
      })
    this.actions$
      .pipe(
        ofActionSuccessful(CreateAddress),
        filter(({ instanceId }) => instanceId === this.instanceId),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(() => {
        this.addressChange.emit()
        this.addressCreated.emit()
      })
    this.actions$
      .pipe(
        ofActionSuccessful(UpdateAddress),
        filter(({ instanceId }) => instanceId === this.instanceId),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(() => {
        this.addressChange.emit()
        this.addressUpdated.emit()
      })

    this.actions$
      .pipe(
        ofActionSuccessful(DeleteAddress),
        filter(({ instanceId }) => instanceId === this.instanceId),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(() => {
        this.addressChange.emit()
        this.addressDeleted.emit()
        // List-row delete never opens the editor, so the editor-close subscription
        // (showEditor$ true→false) doesn't fire. Restore focus directly here so the
        // delete-from-list flow lands on #addAddressBtn (WCAG 2.4.3). For editor-
        // initiated deletes, ShowBlankEditor(false) also triggers the same helper,
        // which simply re-focuses the same element — harmless.
        this.restoreFocusAfterLeavingEditor()
      })
    await this.initializeWidget()
  }

  public enterEditMode(externalFocusReturnId?: string) {
    if (externalFocusReturnId) {
      this.externalFocusReturnId = externalFocusReturnId
    }
    this.store.dispatch(new ShowBlankEditor(true, this.instanceId)).subscribe()
    this.addressEditFormQL.changes.pipe(take(1)).subscribe((components: QueryList<ElementRef>) => {
      this.window.setTimeout(() => {
        const host = components.first.nativeElement as HTMLElement
        if (this.scrollOnAddNew) {
          this.scrollToService.scrollToElement(host, 24)
        }
        host.focus()
      }, 0)
    })
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  isAddAddressesTemplate(): boolean {
    const isLoading = this.store.selectSnapshot(({ addressWidget }) => addressWidget.loading)

    if (isLoading) {
      return false
    }

    const addressIdBeingEdited = this.store.selectSnapshot(
      ({ addressWidget }) => addressWidget.widgetInstances[this.instanceId].addressIdBeingEdited,
    )

    if (addressIdBeingEdited) {
      return false
    }

    const showEditor = this.store.selectSnapshot(({ addressWidget }) => addressWidget.widgetInstances[this.instanceId].editor.visible)
    return !showEditor
  }

  private async initializeWidget() {
    this.instanceId = uuid() as string
    const state = await lastValueFrom(
      this.store.dispatch(new SetInitialConfiguration(this.instanceId, this.mode, this.country, this.locale, this.token)).pipe(
        mergeMap(() => this.store.dispatch(new GetProfile())),
        mergeMap(() => this.store.dispatch(new GetAddresses())),
        take(1),
      ),
    ).catch(() => {
      this.errorWhileInitializing.set(true)
    })

    if (this.mode === MyProfileAddressWidgetMode.CHECKOUT && !state.addressWidget.addresses.length) {
      this.enterEditMode()
    }

    // Set instanced selectors
    this.addressBeingEdited$ = this.store.select(({ addressWidget }) => addressWidget.widgetInstances[this.instanceId].addressIdBeingEdited)
    this.showEditor$ = this.store.select(({ addressWidget }) => addressWidget.widgetInstances[this.instanceId].editor.visible)
    this.selectedAddressId$ = this.store.select(
      ({ addressWidget }) => addressWidget.widgetInstances[this.instanceId].addressList.selectedAddressId,
    )
    this.errorMessage$ = this.store.select(({ addressWidget }) => addressWidget.widgetInstances[this.instanceId].errorMessage)

    // Restore focus when the editor closes (visible -> hidden transition). Mirrors the birthday
    // widget's chain pattern: prefer the in-widget add button if rendered, fall back to the page-level CTA.
    // TODO(focus-fallback): extract this "focus first id that resolves" into a shared pidx-utils helper
    // once all five widgets converge on the same shape.
    this.showEditor$.pipe(startWith(false), pairwise(), takeUntil(this.unsubscribe$)).subscribe(([wasVisible, isVisible]) => {
      if (wasVisible && !isVisible) {
        this.restoreFocusAfterLeavingEditor()
      }
    })

    // Select the initial address item
    const addressIds = this.store
      .selectSnapshot(({ addressWidget }) => addressWidget.addresses)
      .map((address: AddressDto) => address.addressId)

    if (this.initiallySelectedAddressId && addressIds.indexOf(this.initiallySelectedAddressId) !== -1) {
      this.myProfileAddressListService.selectItem(this.initiallySelectedAddressId, this.instanceId)
    } else {
      this.myProfileAddressListService.selectInitialItem(this.instanceId)
    }
  }

  private restoreFocusAfterLeavingEditor() {
    const externalId = this.externalFocusReturnId
    this.externalFocusReturnId = null
    this.window.setTimeout(() => {
      const internal = document.getElementById('addAddressBtn')
      if (internal) {
        internal.focus()
        return
      }
      if (externalId) {
        document.getElementById(externalId)?.focus()
      }
    }, 0)
  }
}
